"use client";
import "./funds_documents.scss";
import { useFunds } from "@/contexts/funds-context";
import { Container } from "@/elements/container/container";
import { Stack } from "@/elements/stack/stack";
import { Text } from "@/elements/text/text";
import { i18n } from "@/model/i18n";
import { DisclosureDownload } from "@/components/disclosure/disclosure_download/disclosure_download";
import { type ReactElement } from "react";

const MONTHLY_REPORT_URLS: Record<number, string> = {
  19: "https://documents.anevis-solutions.com/laiqon/LAIQON_-_AI_Defensive_Multi_Asset_S_ultimo.pdf",
  21: "https://documents.anevis-solutions.com/laiqon/LAIQON_-_AI_Impact_Equity_US_RC_ultimo.pdf",
  23: "https://documents.anevis-solutions.com/laiqon/LAIQON_-_AI_Dynamic_Multi_Asset_S_ultimo.pdf",
  24: "https://documents.anevis-solutions.com/laiqon/LAIQON_-_AI_Impact_Equity_EU_S1_ultimo.pdf",
};

const DOCUMENTS = [
  {
    id: "sales_prospectus",
    title: i18n.t("pages.funds.documents.sales_prospectus.title"),
    subtitle: i18n.t("pages.funds.documents.sales_prospectus.subtitle"),
    href: (id?: number) =>
      `https://files.laic.de/documents/funds/fund_id_${id}_sales_prospect.pdf`,
  },
  {
    id: "monthly_report",
    title: i18n.t("pages.funds.documents.monthly_report.title"),
    subtitle: i18n.t("pages.funds.documents.monthly_report.subtitle"),
    href: (id?: number) =>
      (id !== undefined && MONTHLY_REPORT_URLS[id]) ||
      `https://files.laic.de/documents/funds/fund_id_${id}_monatsreport.pdf`,
  },
];

export function FundsDocuments(): ReactElement | null {
  const { selectedFund } = useFunds();

  if (!selectedFund) return null;

  const tranchesDocuments =
    selectedFund?.tranches.map((tranche) => ({
      id: tranche.id,
      title: i18n.t("pages.funds.documents.key_information_document", {
        trancheName: tranche.name,
      }),
      subtitle: "PDF | 458 KB",
      href: () =>
        `https://files.laic.de/documents/funds/fund_id_${selectedFund.id}_fund_tranche_id_${tranche.id}_basic_information.pdf`,
    })) || [];

  const allDocuments = [DOCUMENTS[0], ...tranchesDocuments, DOCUMENTS[1]];

  return (
    <section className="funds-documents" aria-label="funds-documents">
      <Container>
        <Stack size={40} tabletSize={60} desktopSize={80}>
          <section
            className="funds-documents__section"
            aria-label="funds-documents-literature"
          >
            <Text
              type="body-xbold"
              color="darker-pairings-primary"
              htmlElement="h2"
            >
              {i18n.t("pages.funds.documents.literature")}
            </Text>
            <Stack size={24}>
              {allDocuments.map((document) => (
                <DisclosureDownload
                  padding={8}
                  key={document.id}
                  href={document.href(selectedFund?.id)}
                  title={document.title}
                  subtitle={document.subtitle}
                  ariaLabel={i18n.t("pages.funds.documents.alt_text", {
                    title: document.title,
                  })}
                />
              ))}
            </Stack>
          </section>
          <section
            className="funds-documents__section"
            aria-label="funds-documents-disclaimer"
          >
            <Text
              type="body-xbold"
              color="darker-pairings-primary"
              htmlElement="h3"
            >
              {i18n.t("pages.funds.documents.disclaimer")}
            </Text>
            <Text type="caption" color="neutral-on-surface" htmlElement="p">
              {i18n.t("pages.funds.documents.disclaimer_text_1")}
              <br />
              <br />
              {i18n.t("pages.funds.documents.disclaimer_text_2")}
              <br />
              <br />
              {i18n.t("pages.funds.documents.disclaimer_text_3")}
            </Text>
          </section>
        </Stack>
      </Container>
    </section>
  );
}
